import { IconPound } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import { formatCurrency } from '../../../../../components/Input/formatCurrency';
import { HookFormInput } from '../../../../../components/Input/Input';
import { useUpdateTurnover } from '../../../hooks/useUpdateTurnover';

type FormValues = {
  turnover: string;
};

export const UpdateTurnover = ({
  refreshSpendingLimit,
  loadingRefreshSpendingLimit,
}: {
  refreshSpendingLimit: () => Promise<void>;
  loadingRefreshSpendingLimit: boolean;
}) => {
  const { updateTurnover, loadingTurnover: loadingUpdateTurnover } =
    useUpdateTurnover();

  const methods = useForm<FormValues>({
    defaultValues: {
      turnover: '',
    },
  });

  const handleSubmit = async ({ turnover }: FormValues) => {
    const sanitizedTurnover = turnover.replace(/,/g, '');
    await updateTurnover(Number(sanitizedTurnover));
    await refreshSpendingLimit();
  };

  const renderButtonText = () => {
    if (loadingUpdateTurnover) {
      return (
        <>
          <svg
            className="h-l w-l animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          Updating turnover...
        </>
      );
    }
    if (loadingRefreshSpendingLimit) {
      return (
        <>
          <svg
            className="h-l w-l animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          Checking limits...
        </>
      );
    }
    return 'See what you can spend';
  };

  const onSubmit = methods.handleSubmit(handleSubmit);

  return (
    <FormProvider {...methods}>
      <form className="border-t border-t-structure-80" onSubmit={onSubmit}>
        <div className="flex flex-col gap-2xs">
          <div className="pt-xl text-2xl font-bold">
            First, we need to check some numbers
          </div>
          <div className="pt-s font-reg">
            It's been a while since we asked you for an update.
          </div>
        </div>
        <div className="pt-l">
          <div className="text-m font-med">Turnover for the last 12 months</div>
          <div className="text-s font-reg">
            This doesn't have to be exact to the penny
          </div>
        </div>
        <div className="pt-m">
          <HookFormInput
            name="turnover"
            placeholder="Enter amount..."
            iconLeft={<IconPound />}
            formatter={formatCurrency}
            showError={false}
          />
        </div>
        <div className="pt-l">
          <Button
            className="w-full"
            variant="primary"
            disabled={loadingUpdateTurnover || loadingRefreshSpendingLimit}
          >
            {renderButtonText()}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
